import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchPasswordResetStatus, PasswordResetStatusResponse, resetPasswordWithToken } from '../services/usersApi';

function passwordErrorLabel(code: string): string {
  switch (String(code || '').trim()) {
    case 'min_length':
      return 'Password is too short.';
    case 'require_upper':
      return 'Add at least one uppercase letter.';
    case 'require_lower':
      return 'Add at least one lowercase letter.';
    case 'require_digit':
      return 'Add at least one number.';
    case 'require_symbol':
      return 'Add at least one symbol.';
    case 'contains_username':
      return 'Password cannot include your username.';
    case 'contains_email':
      return 'Password cannot include part of your email address.';
    case 'reuse_forbidden':
      return 'New password must be different from the current password.';
    default:
      return code;
  }
}

function passwordPolicyLines(policy?: PasswordResetStatusResponse['password_policy']): string[] {
  if (!policy) return [];
  const lines = [`At least ${policy.min_length} characters`];
  if (policy.require_upper) lines.push('At least one uppercase letter');
  if (policy.require_lower) lines.push('At least one lowercase letter');
  if (policy.require_digit) lines.push('At least one number');
  if (policy.require_symbol) lines.push('At least one symbol');
  lines.push('Must not include your username');
  lines.push('Must not include part of your email address');
  return lines;
}

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = String(searchParams.get('token') || '').trim();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [info, setInfo] = useState<PasswordResetStatusResponse | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) {
        setError('Reset token is missing.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        setPasswordErrors([]);
        const result = await fetchPasswordResetStatus(token);
        if (cancelled) return;
        setInfo(result);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.response?.data?.error || err?.message || 'Failed to validate reset link');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) return;
    if (!password) {
      setError('Password is required.');
      setPasswordErrors([]);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setPasswordErrors([]);
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      setPasswordErrors([]);
      await resetPasswordWithToken({ token, password });
      setSuccess('Password reset complete. You can now sign in.');
      setTimeout(() => navigate('/login'), 1200);
    } catch (err: any) {
      const data = err?.response?.data || {};
      setError(data?.error || err?.message || 'Password reset failed');
      setPasswordErrors(
        Array.isArray(data?.details) ? data.details.map((item: string) => passwordErrorLabel(item)) : []
      );
    } finally {
      setSubmitting(false);
    }
  };

  const policyLines = passwordPolicyLines(info?.password_policy);

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ mt: 8, mb: 6 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            Reset password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose a new password for your account.
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="body2">{error === 'password_policy_failed' ? 'Password does not meet the required policy.' : error}</Typography>
                    {passwordErrors.length > 0 && (
                      <List dense disablePadding>
                        {passwordErrors.map((item) => (
                          <ListItem key={item} disableGutters sx={{ py: 0 }}>
                            <ListItemText primary={`- ${item}`} />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Stack>
                </Alert>
              )}
              {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
              {info && (
                <Stack spacing={1} sx={{ mb: 3 }}>
                  <Typography variant="body2"><strong>User:</strong> {info.user.username}</Typography>
                  <Typography variant="body2"><strong>Email:</strong> {info.user.email}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reset link expires {new Date(info.reset.expires_at).toLocaleString()}
                  </Typography>
                </Stack>
              )}
              {policyLines.length > 0 && !success && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Password requirements:
                  </Typography>
                  <List dense disablePadding>
                    {policyLines.map((line) => (
                      <ListItem key={line} disableGutters sx={{ py: 0 }}>
                        <ListItemText primary={`- ${line}`} />
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              )}
              {info && !success && (
                <Box component="form" onSubmit={handleSubmit}>
                  <Stack spacing={2}>
                    <TextField
                      label="New password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Confirm password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      fullWidth
                    />
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button variant="outlined" onClick={() => navigate('/login')}>
                        Back to login
                      </Button>
                      <Button type="submit" variant="contained" disabled={submitting}>
                        {submitting ? <CircularProgress size={20} /> : 'Reset password'}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPassword;
